// src/hooks/useNodeActions.js
import { useState, useCallback, useRef } from 'react';
import { useReactFlow } from 'reactflow';
import * as C from '../constants';
import api from '../api';
import localCacheService from '../services/localCacheService';
import { formatShortTitle, calculateLongSide, calculateLongSide500, calculateLongSide120 } from '../editorUtils';
import { assembleStoryboardPrompt, LOCK_REF_NAME } from '../storyboardParser';
import { findPreset, findSkillboxPreset } from '../utils/presetTree';

/**
 * 节点操作回调：更新、删除、替换、重新上传、剥离、生成等。
 *
 * @param {object} deps
 * @param {Function} deps.setNodes
 * @param {Function} deps.setEdges
 * @param {Function} deps.setAlertMsg
 * @param {Function} deps.setMenuData
 * @param {Function} deps.setDeleteConfirm
 * @param {object}   deps.deleteConfirm
 * @param {object}   deps.nodesRef
 * @param {object}   deps.crewIdRef
 * @param {object}   deps.crewNameRef
 * @param {string}   deps.crewId
 * @param {string}   deps.crewName
 * @param {string}   deps.projectId
 * @param {object}   deps.replaceInputRef
 * @param {object}   deps.activeBlobsRef
 * @param {object}   deps.globalAssetsRef
 * @param {object}   deps.publicAssetsRef
 * @param {object}   deps.callbacksRef
 * @param {Function} deps.saveProject
 */
const useNodeActions = (deps) => {
  const {
    setNodes, setEdges: setEdgesState, setAlertMsg, setMenuData, setDeleteConfirm, deleteConfirm,
    nodesRef, crewIdRef, crewNameRef, projectId,
    replaceInputRef, activeBlobsRef, globalAssetsRef, publicAssetsRef,
    callbacksRef,
    saveProject, isMountedRef, activeProjectIdRef,
  } = deps;

  const { getNodes, getEdges, setEdges: setFlowEdges, deleteElements, screenToFlowPosition } = useReactFlow();

  // --- 上传中止控制器注册表 ---
  // key: nodeId, value: AbortController
  // 节点删除时调用 controller.abort() 立即中止正在进行的云存储上传
  const uploadAbortControllersRef = useRef(new Map());

  // --- Blob 管理 ---
  const createSecureBlob = useCallback((file) => {
    const url = URL.createObjectURL(file);
    activeBlobsRef.current.add(url);
    return url;
  }, [activeBlobsRef]);

  // 🛡️ revokeSecureBlob：释放 blob URL 并从跟踪集中移除
  // 允许释放未被 activeBlobsRef 跟踪的 blob URL（如 ImageNodeView 直接创建的）
  const revokeSecureBlob = useCallback((url, force = false) => {
    if (!url || !url.startsWith('blob:')) return;
    const inGlobal = globalAssetsRef.current.some(a => a.url === url || a.thumbnailUrl === url);
    const inPublic = publicAssetsRef.current.some(a => a.url === url || a.thumbnailUrl === url);
    if ((inGlobal || inPublic) && !force) return; // 🛡️ force=true 时跳过保护，强制 revoke

    URL.revokeObjectURL(url);
    activeBlobsRef.current.delete(url); // Set.delete 对不存在的 key 是 no-op
  }, [activeBlobsRef, globalAssetsRef, publicAssetsRef]);

  // ── handleUpdateNode: 更新节点数据 + 自动同步所有引用该节点的下游节点 ──
  // 关键：修改某节点的 output/title 后，需要同步更新所有将其作为 refFile/refText/refAudio 的下游节点
  // 使用函数式 setNodes 确保基于最新 state（避免 stale closure 问题）
  const handleUpdateNode = useCallback((nodeId, newData) => {
    // 性能优化：判断本次更新是否需要同步到下游引用节点
    // 只有 title / output / localCacheUrl / bgColor / prompt(refTexts) / description 变化时才需要遍历下游
    const needsRefSync = newData.title !== undefined || newData.output !== undefined ||
                         newData.localCacheUrl !== undefined || newData.bgColor !== undefined ||
                         newData.prompt !== undefined || newData.thumbUrl !== undefined ||
                         newData.description !== undefined;

    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (node.id === nodeId) {
          // 比例预览：底部锚定 —— 调整 position.y 使控制台不动
          // 🔑 保留 _preserveSize 标记：更新尺寸时不能丢失这个标记，否则会触发二次重算
          let updatedNode = {
            ...node,
            data: {
              ...node.data,
              ...newData,
              // 如果原 data 有 _preserveSize 且 newData 没覆盖它，保留原值
              _preserveSize: newData._preserveSize !== undefined ? newData._preserveSize : node.data._preserveSize
            }
          };
          if (newData._ratioPreview && newData.customSize) {
            const oldH = node.data.customSize?.height || 240;
            const newH = newData.customSize.height;
            if (oldH !== newH) {
              updatedNode.position = { ...node.position, y: node.position.y + (oldH - newH) };
            }
          }
          return updatedNode;
        }

        // 如果不需要引用同步（如仅更新 params / modelId / loading 等），直接返回原节点
        if (!needsRefSync) return node;

        let hasChange = false;
        let updatedData = { ...node.data };

        // 同步 refTexts
        if (updatedData.refTexts && updatedData.refTexts.some(rt => rt.id === nodeId)) {
          updatedData.refTexts = updatedData.refTexts.map(rt => (rt.id === nodeId ? {
            ...rt,
            title: newData.title !== undefined ? newData.title : rt.title,
            bgColor: newData.bgColor !== undefined ? newData.bgColor : rt.bgColor,
            text: newData.prompt !== undefined ? newData.prompt : rt.text
          } : rt));
          hasChange = true;
        }

        // 同步 refFiles
        if (updatedData.refFiles && updatedData.refFiles.some(rf => rf.id === nodeId)) {
          updatedData.refFiles = updatedData.refFiles.map(rf => (rf.id === nodeId ? {
            ...rf,
            // 🎬 分镜草稿条目：title 写死为"分镜草稿"，不随分镜节点改名/标题变化
            title: rf.isStoryboardDraft ? rf.title : (newData.title !== undefined ? newData.title : rf.title),
            url: newData.output !== undefined ? newData.output : rf.url,
            // 🔧 localCacheUrl 遮蔽修复：
            //   渲染公式 src = localCacheUrl || url，如果 output 变了但旧 localCacheUrl 还在，
            //   下游会继续显示旧缓存图片。当 output 变化且未显式传 localCacheUrl 时，清除旧值。
            localCacheUrl: newData.localCacheUrl !== undefined ? newData.localCacheUrl : (newData.output !== undefined ? undefined : rf.localCacheUrl),
            // 🖼️ thumbUrl 同步：上游重出图时下游参考缩略图跟着换；output 变而未带新 thumbUrl 则清旧值，避免缩略图对不上
            thumbUrl: newData.thumbUrl !== undefined ? newData.thumbUrl : (newData.output !== undefined ? undefined : rf.thumbUrl),
            // 📝 description 同步：上游修改描述词时下游参考也要跟着更新
            description: newData.description !== undefined ? newData.description : rf.description
          } : rf));
          hasChange = true;
        }

        // 同步 refAudios
        if (updatedData.refAudios && updatedData.refAudios.some(ra => ra.id === nodeId)) {
          updatedData.refAudios = updatedData.refAudios.map(ra => (ra.id === nodeId ? {
            ...ra,
            title: newData.title !== undefined ? newData.title : ra.title,
            url: newData.output !== undefined ? newData.output : ra.url,
            // 🔧 同上：output 变化时清除下游旧 localCacheUrl，防止缓存遮蔽新 URL
            localCacheUrl: newData.localCacheUrl !== undefined ? newData.localCacheUrl : (newData.output !== undefined ? undefined : ra.localCacheUrl),
            // 📝 description 同步：上游修改描述词时下游参考也要跟着更新
            description: newData.description !== undefined ? newData.description : ra.description
          } : ra));
          hasChange = true;
        }

        return hasChange ? { ...node, data: updatedData } : node;
      });

      nodesRef.current = updatedNodes;
      return updatedNodes;
    });
  }, [setNodes, nodesRef]);

  // --- 中止指定节点的上传 ---
  const abortNodeUpload = useCallback((nodeId) => {
    const controller = uploadAbortControllersRef.current.get(nodeId);
    if (controller) {
      controller.abort();
      uploadAbortControllersRef.current.delete(nodeId);
      console.log(`🛑 已中止节点 ${nodeId} 的上传`);
    }
  }, []);

  // --- 删除节点 ---
  const handleDeleteNodeButton = useCallback((nodeId) => {
    const node = getNodes().find(n => n.id === nodeId);
    // 正在 AI 生成 或 正在上传 的媒体节点：弹确认框
    const isBusy = node?.data?.loading || node?.data?.uploading;
    if (node && isBusy && (node.data?.type === 'image' || node.data?.type === 'video')) {
      setDeleteConfirm({
        nodeIds: [nodeId],
        nodeTitle: node.data?.title || (node.data?.type === 'video' ? '视频节点' : '图像节点'),
        isUploading: !!node.data?.uploading && !node.data?.loading,
      });
      return;
    }
    deleteElements({ nodes: [{ id: nodeId }] });
  }, [deleteElements, getNodes, setDeleteConfirm]);

  const handleDeleteSelectedNodes = useCallback(() => {
    const selected = getNodes().filter(n => n.selected);
    if (selected.length === 0) return;

    // 正在 AI 生成 或 正在上传 的媒体节点都需要确认
    const busyMediaNodes = selected.filter(
      n => (n.data?.loading || n.data?.uploading) && (n.data?.type === 'image' || n.data?.type === 'video')
    );

    if (busyMediaNodes.length > 0) {
      const titles = busyMediaNodes.map(
        n => n.data?.title || (n.data?.type === 'video' ? '视频节点' : '图像节点')
      );
      // 判断是否全部为上传中（非 AI 生成）
      const allUploading = busyMediaNodes.every(n => n.data?.uploading && !n.data?.loading);
      setDeleteConfirm({
        nodeIds: selected.map(n => n.id),
        nodeTitle: busyMediaNodes.length === 1
          ? titles[0]
          : `${busyMediaNodes.length} 个正在处理的节点`,
        loadingCount: busyMediaNodes.length,
        isUploading: allUploading,
      });
      return;
    }

    deleteElements({ nodes: selected.map(n => ({ id: n.id })) });
  }, [deleteElements, getNodes, setDeleteConfirm]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirm?.nodeIds?.length > 0) {
      // 🛑 中止所有被删除节点正在进行的上传
      deleteConfirm.nodeIds.forEach(id => abortNodeUpload(id));
      deleteElements({ nodes: deleteConfirm.nodeIds.map(id => ({ id })) });
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, deleteElements, setDeleteConfirm]);

  // --- 移除引用 ---
  const handleRemoveRefAudio = useCallback((targetId, audioId) => {
    setNodes(nds => nds.map(node => {
      if (node.id !== targetId) return node;
      // 🔓 释放该参考音频 blob 的保活引用（与 onConnect 中的 pin 对称）
      const removed = (node.data.refAudios || []).find(a => a.id === audioId);
      if (removed?.localCacheUrl) localCacheService.unpinBlobUrl(removed.localCacheUrl);
      return { ...node, data: { ...node.data, refAudios: (node.data.refAudios || []).filter(a => a.id !== audioId) } };
    }));
    setFlowEdges(eds => eds.filter(edge => !(edge.target === targetId && edge.source === audioId)));
  }, [setNodes, setFlowEdges]);

  const handleRemoveRefFile = useCallback((targetId, fileId) => {
    // 🔍 先获取被删除的 refFile 信息，判断是否需要断开连线
    let shouldRemoveEdge = false;
    const nodes = getNodes();
    const targetNode = nodes.find(n => n.id === targetId);

    if (targetNode) {
      const removed = (targetNode.data.refFiles || []).find(f => f.id === fileId);

      // 判断是否应该删除边：
      // ❌ 分镜草稿图（isStoryboardDraft）→ 不删边
      // ❌ 间接引用（边不存在）→ 不删边
      // ✅ 只有"直接连线且非特殊标记"时才删边
      if (removed && !removed.isStoryboardDraft) {
        // 检查边是否真实存在
        const edges = getEdges();
        const edgeExists = edges.some(edge =>
          edge.target === targetId && edge.source === fileId
        );
        shouldRemoveEdge = edgeExists;
      }
    }

    setNodes(nds => nds.map(node => {
      if (node.id !== targetId) return node;
      // 🔓 释放该参考图 blob 的保活引用（与 onConnect 中的 pin 对称，避免引用泄漏）
      const removed = (node.data.refFiles || []).find(f => f.id === fileId);
      if (removed?.localCacheUrl) localCacheService.unpinBlobUrl(removed.localCacheUrl);
      return { ...node, data: { ...node.data, refFiles: (node.data.refFiles || []).filter(f => f.id !== fileId) } };
    }));

    // 🔗 只有在明确应该删除边的情况下才删除
    if (shouldRemoveEdge) {
      setFlowEdges(eds => eds.filter(edge => !(edge.target === targetId && edge.source === fileId)));
    }
  }, [setNodes, setFlowEdges, getNodes, getEdges]);

  const handleRemoveRefText = useCallback((targetId, textId) => {
    setNodes(nds => nds.map(node =>
      node.id === targetId
        ? { ...node, data: { ...node.data, refTexts: (node.data.refTexts || []).filter(t => t.id !== textId) } }
        : node
    ));
    setFlowEdges(eds => eds.filter(edge => !(edge.target === targetId && edge.source === textId)));
  }, [setNodes, setFlowEdges]);

  // --- Reupload ---
  const handleReuploadLogic = useCallback((sid, url, w, h) => {
    const node = getNodes().find(n => n.id === sid);
    if (node) revokeSecureBlob(node.data.output);

    const updateData = { output: url, outputs: [url] };
    if (w && h && node && node.data.type !== 'audio') {
      updateData.customSize = (node.data.isDirectUpload || node.data.assetId)
        ? calculateLongSide120(w, h)
        : calculateLongSide500(w, h);
    }
    // 通过 handleUpdateNode 更新，自动传播到下游 refFiles/refAudios
    handleUpdateNode(sid, updateData);
  }, [getNodes, revokeSecureBlob, handleUpdateNode]);

  // --- Replace ---
  const [replacingNodeId, setReplacingNodeId] = useState(null);

  const handleReplace = useCallback((id, type) => {
    setReplacingNodeId({ id, type });
    if (replaceInputRef.current) {
      if (type === 'video') {
        replaceInputRef.current.accept = 'video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska';
      } else if (type === 'audio') {
        replaceInputRef.current.accept = 'audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,audio/aac,audio/flac,audio/webm,audio/x-wav';
      } else {
        replaceInputRef.current.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
      }
      replaceInputRef.current.value = '';
      replaceInputRef.current.click();
    }
  }, [replaceInputRef]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // uploadAndReplace — 文件上传核心流程
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 设计目标：确保文件在任何网络条件下都不会丢失
  //
  // 数据安全层级（由快到慢）：
  //   1. blob URL    → 当前会话显示（关闭标签页即失效）
  //   2. local-cache → IndexedDB + 本地文件系统（跨会话持久化）
  //   3. 云存储 URL  → R2/OSS（跨设备持久化）
  //
  // 流程：
  //   ① 生成 blob URL（立即显示） + 写入 local-cache（持久化备份）
  //   ② 设置 output = local-cache://... URL（可被 auto-save 安全保存到数据库）
  //   ③ 后台循环重试 presignUpload（最多 MAX_UPLOAD_RETRIES=20 次）
  //   ④ 上传成功 → 更新 output 为云存储 URL + 删除临时本地缓存
  //   ⑤ 超限或 OSS_UNAVAILABLE → fallback 保持 local-cache URL
  //
  // 为什么不用 output = blob URL？
  //   → blob URL 不能持久化，会话结束就失效
  //   → 如果 autoSave 把 blob:// 写入数据库，下次打开该节点会白屏
  //   → local-cache:// URL 可以持久化，且下次打开时能通过本地缓存恢复
  //
  // 退出条件（不再重试）：
  //   - isMountedRef.current = false（组件已卸载）
  //   - 节点已被用户删除
  //   - 项目已切换（activeProjectIdRef 变化）
  //   - attempt >= MAX_UPLOAD_RETRIES
  //   - OSS_UNAVAILABLE 错误（云存储未配置，无意义重试）
  // existingOutputs：降级模式专用，把新图插到图组首位并保留原图，跳过 customSize 更新
  const uploadAndReplace = useCallback(async (nodeId, file, blobUrl, customTitle, existingOutputs) => {
    // 🔧 使用 ref 获取当前团队信息（避免闭包捕获过期值，稳定回调引用）
    const currentCrewId = crewIdRef.current;
    const currentCrewName = crewNameRef.current;
    const displayTitle = customTitle || file.name;

    // 🆕 为该节点创建 AbortController，用于删除时中止上传
    const uploadController = new AbortController();
    uploadAbortControllersRef.current.set(nodeId, uploadController);
    
    // 🆕 生成唯一文件名（用于本地缓存，无论云存储是否配置）
    const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
    const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}${ext}`;
    const fileType = localCacheService.getFileTypeFromMime(file.type);
    
    // 🛡️ 关键修复：在任何 await 之前，立即同步设置 local-cache:// URL 作为 output
    // 旧逻辑：先 await writeFileToCache 再设 output → 异步间隙内 output 仍为 blob URL
    //         若此时触发保存（手动/自动），buildCleanNodes 会清除 blob URL → 图片丢失
    // 新逻辑：先同步设 output = local-cache://...（可持久化），再异步写缓存
    const typeFolder = fileType === 'image' ? 'images' : fileType === 'video' ? 'videos' : 'audios';
    const localCachePersistUrl = `local-cache://${currentCrewId}/${typeFolder}/${uniqueFileName}`;
    handleUpdateNode(nodeId, {
      uploading: true,
      title: `上传中... ${displayTitle}`,
      output: localCachePersistUrl,
      outputs: existingOutputs ? [localCachePersistUrl, ...existingOutputs] : [localCachePersistUrl],
      localCacheUrl: blobUrl || undefined,
      isDirectUpload: true,
      _preserveSize: existingOutputs ? true : undefined, // 降级模式：阻止 ImageNodeView.onLoad 重算尺寸
    });
    
    // 🆕 后台写入本地缓存（IndexedDB + 本地文件夹），确保即使云存储失败也有备份
    try {
      await localCacheService.writeFileToCache(currentCrewId, currentCrewName, fileType, uniqueFileName, file);
      console.log('📦 已预缓存到本地: ' + uniqueFileName);
    } catch (e) {
      console.warn('本地预缓存失败:', e);
    }
    
    // 测量尺寸（使用 blob URL，不阻塞上传）；降级模式保留原节点尺寸，跳过
    if (existingOutputs) { /* 降级就地替换，不重算节点卡片尺寸 */ }
    else if (file.type && file.type.startsWith('video/')) {
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => { handleUpdateNode(nodeId, { customSize: calculateLongSide(vid.videoWidth, vid.videoHeight, 240) }); vid.src = ''; vid.load(); };
      vid.src = blobUrl || localCachePersistUrl;
    } else if (file.type && file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => { handleUpdateNode(nodeId, { customSize: calculateLongSide(img.width, img.height, 240) }); img.src = ''; };
      img.src = blobUrl || localCachePersistUrl;
    }
    
    // 🔁 持续重试云存储上传，直到成功或组件卸载/项目切换
    // ⚡ 最多重试 MAX_UPLOAD_RETRIES 次，超限后 fallback 到本地缓存
    const MAX_UPLOAD_RETRIES = 20;
    const attemptUpload = async () => {
      let attempt = 0;
      while (isMountedRef?.current && attempt < MAX_UPLOAD_RETRIES) {
        // 🛡️ 检查节点是否已被删除
        const nodeStillExists = nodesRef?.current?.some(n => n.id === nodeId);
        if (!nodeStillExists) {
          console.log(`🛡️ 节点 ${nodeId} 已删除，停止上传 ${uniqueFileName}`);
          return;
        }
        // 检查项目是否已切换
        const stillSameProject = activeProjectIdRef?.current && activeProjectIdRef.current === projectId;
        if (!stillSameProject) {
          console.log(`🛡️ 项目已切换，停止上传 ${uniqueFileName}`);
          return;
        }
        attempt++;
        try {
          const data = await api.presignUpload(file, 'uploads/', { signal: uploadController.signal });
          if (data.url) {
            // 🔧 不要立即用旧的 blob URL 更新，先获取新的缓存 URL
            const ossFileName = localCacheService.extractFileNameFromUrl(data.url);
            
            // 先写入新的云存储文件缓存
            if (ossFileName && ossFileName !== uniqueFileName) {
              await localCacheService.writeFileToCache(currentCrewId, currentCrewName, fileType, ossFileName, file)
                .catch(err => console.warn('缓存写入失败:', err));
            }
            
            // 🔧 获取新的本地缓存 URL（确保总是有效的 blob URL）
            const cacheResult = await localCacheService.getLocalUrlOrFetch(data.url, currentCrewId, currentCrewName, fileType);
            
            // 🛡️ 优先保留当前正在显示的 blob URL，避免 URL 变更导致图片闪烁
            // 只有当原始 blobUrl 无效时才用缓存的新 URL
            const currentNode = nodesRef?.current?.find(n => n.id === nodeId);
            const existingCacheUrl = currentNode?.data?.localCacheUrl;
            const keepExistingUrl = existingCacheUrl && existingCacheUrl.startsWith('blob:');
            
            // 一次性更新所有字段
            handleUpdateNode(nodeId, {
              uploading: false,
              title: displayTitle,
              output: data.url,
              outputs: existingOutputs ? [data.url, ...existingOutputs] : [data.url],
              localCacheUrl: keepExistingUrl ? existingCacheUrl : (cacheResult.isLocal ? cacheResult.url : (blobUrl || undefined)),
              isDirectUpload: true,
              _preserveSize: undefined, // 上传完成，解除尺寸保护
              // 🆕 派生字段（缩略图/视频海报/宽高体积）——自建化：画布显示不依赖 OSS 现算
              ...(data.thumbUrl ? { thumbUrl: data.thumbUrl } : {}),
              ...(data.posterUrl ? { posterUrl: data.posterUrl } : {}),
              ...(data.width && data.height ? { width: data.width, height: data.height } : {}),
              ...(data.fileBytes ? { fileBytes: data.fileBytes } : {}),
            });

            setAlertMsg({ text: "上传成功", type: "success" });
            setTimeout(() => saveProject(false), 500);

            // 删除旧的临时缓存文件（如果文件名不同）
            if (ossFileName && ossFileName !== uniqueFileName) {
              const stillSame = activeProjectIdRef?.current && activeProjectIdRef.current === projectId;
              if (isMountedRef?.current && stillSame) {
                localCacheService.deleteFileFromCache(currentCrewId, currentCrewName, fileType, uniqueFileName)
                  .catch(() => {});
              } else {
                console.log(`🛡️ 跳过删除临时缓存 ${uniqueFileName}（项目已切换或组件已卸载）`);
              }
            }
            return; // 上传成功，退出
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            // 🛑 上传被主动中止（节点已删除），不重试、不提示
            console.log(`🛑 节点 ${nodeId} 上传已中止`);
            uploadAbortControllersRef.current.delete(nodeId);
            return;
          }
          if (err.code === 'OSS_UNAVAILABLE') {
            console.warn("云存储未配置，使用本地缓存");
            // 🔧 获取本地缓存 URL
            const cacheResult = await localCacheService.getLocalUrlOrFetch(localCachePersistUrl, currentCrewId, currentCrewName, fileType);
            handleUpdateNode(nodeId, { 
              uploading: false, 
              title: displayTitle,
              localCacheUrl: cacheResult.isLocal ? cacheResult.url : (blobUrl || undefined)
            });
            setAlertMsg({ text: "已保存到本地缓存（建议配置云存储以同步到云端）", type: "warning" });
            setTimeout(() => saveProject(false), 500);
            return;
          }
          // 指数退避：5s → 10s → 20s → ... → 120s 封顶
          const delay = Math.min(5000 * Math.pow(2, attempt - 1), 120000);
          console.warn(`⚠️ 云存储上传第 ${attempt} 次失败 (${uniqueFileName}): ${err.message}，${delay / 1000}s 后重试...`);
          if (attempt === 1) {
            setAlertMsg({ text: "上传中，网络较慢将自动重试...", type: "warning" });
          }
          await new Promise(r => setTimeout(r, delay));
        }
      }
      // ⚠️ 重试次数用尽但仍未成功 → fallback 到本地缓存
      if (isMountedRef?.current && attempt >= MAX_UPLOAD_RETRIES) {
        console.warn(`❌ 云存储上传超过 ${MAX_UPLOAD_RETRIES} 次重试，降级为本地缓存: ${uniqueFileName}`);
        const cacheResult = await localCacheService.getLocalUrlOrFetch(localCachePersistUrl, currentCrewId, currentCrewName, fileType);
        handleUpdateNode(nodeId, {
          uploading: false,
          title: displayTitle,
          localCacheUrl: cacheResult.isLocal ? cacheResult.url : (blobUrl || undefined)
        });
        setAlertMsg({ text: "云存储上传失败，已暂存到本地缓存", type: "warning" });
        setTimeout(() => saveProject(false), 500);
      }
    };
    attemptUpload().finally(() => {
      uploadAbortControllersRef.current.delete(nodeId);
    });
  }, [handleUpdateNode, saveProject, crewIdRef, crewNameRef, setAlertMsg]);

  // --- handleDownscaleInPlace ---
  // 降分辨率/压缩就地替换：把新图插入图组首位（触发下游 refFiles 自动替换），原图保留为副图
  const handleDownscaleInPlace = useCallback(async (nodeId, blob, blobUrl) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    const existingOutputs = node.data.outputs?.length
      ? node.data.outputs
      : (node.data.output ? [node.data.output] : []);
    // 🔧 扩展名/MIME 必须与 blob 实际格式一致：降分辨率恒为 png，压缩可能是 jpeg/webp。
    //    用 .png 装 jpeg/webp 会让 OSS 回传错误 content-type，导致部分浏览器解码失败。
    const mime = blob.type || 'image/png';
    const ext = mime.includes('webp') ? 'webp' : (mime.includes('jpeg') || mime.includes('jpg')) ? 'jpg' : 'png';
    const file = new File([blob], `downscaled-${Date.now()}.${ext}`, { type: mime });
    await uploadAndReplace(nodeId, file, blobUrl, node.data.title, existingOutputs);
  }, [nodesRef, uploadAndReplace]);

  // --- handleReplaceFileChange ---
  const handleReplaceFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingNodeId) return;

    const { id, type } = replacingNodeId;

    // 文件大小检查（视频/音频）
    if ((type === 'video' || type === 'audio') && file.size > C.MAX_UPLOAD_SIZE) {
      setAlertMsg({ text: `文件 ${file.name} 超过 ${C.MAX_UPLOAD_SIZE_LABEL} 限制`, type: 'error' });
      e.target.value = '';
      return;
    }

    const targetNode = getNodes().find(n => n.id === id);
    if (!targetNode) return;

    const oldUrl = targetNode.data.output;
    const oldTitle = targetNode.data.title || '未命名';
    const tempUrl = createSecureBlob(file);
    const useSmall = targetNode?.data?.isDirectUpload || targetNode?.data?.assetId;
    // 生成节点（非直传、非素材库、非分离）不因"更换素材"变为普通上传节点
    const isOriginallyGenerateNode = !targetNode.data.isDirectUpload && !targetNode.data.assetId && !targetNode.data.isDetached;

    const doUpload = async (size) => {
      handleUpdateNode(id, { uploading: true, output: tempUrl, customSize: size, title: `上传中... ${file.name}` });
      try {
        const data = await api.presignUpload(file);
        if (data.url) {
          // 🔧 保留 tempUrl 作为 localCacheUrl，确保前端操作使用本地缓存
          const replaceFields = {
            uploading: false, output: data.url, outputs: [data.url], title: file.name,
            customSize: size, localCacheUrl: tempUrl
          };
          // 非生成节点才标记为直传节点，生成节点保持原有身份不变
          if (!isOriginallyGenerateNode) {
            replaceFields.isDirectUpload = true;
            replaceFields.assetId = null;
            replaceFields.isDetached = false;
            replaceFields.fromModelNode = false;
          }
          handleUpdateNode(id, replaceFields);
          if (oldUrl && oldUrl.startsWith('blob:')) revokeSecureBlob(oldUrl);
          // 不立即删除旧文件 — 文件在删除项目时按文件夹整体清理
          setAlertMsg({ text: type === 'video' ? "视频已更换并上传" : type === 'audio' ? "音频已更换并上传" : "素材已更换并上传", type: "success" });
          const fileType = localCacheService.getFileTypeFromMime(file.type);
          const fileName = localCacheService.extractFileNameFromUrl(data.url);
          if (fileName) localCacheService.writeFileToCache(crewIdRef.current, crewNameRef.current, fileType, fileName, file).catch(() => {});
          setTimeout(() => saveProject(false), 500);
        }
      } catch (err) {
        console.error("上传失败", err);
        handleUpdateNode(id, { uploading: false, title: oldTitle, output: oldUrl });
        revokeSecureBlob(tempUrl);
        const errMsg = err?.message || '上传失败';
        setAlertMsg({ text: errMsg.includes('云存储') || errMsg.includes('OSS') ? '请先在设置中配置云存储' : `上传失败，已恢复原素材`, type: "error" });
      }
    };

    if (type === 'image') {
      const img = new Image();
      img.onload = () => {
        const size = useSmall ? calculateLongSide120(img.width, img.height) : calculateLongSide500(img.width, img.height);
        doUpload(size);
        img.src = '';
      };
      img.src = tempUrl;
    } else if (type === 'video') {
      const vid = document.createElement('video');
      vid.onloadedmetadata = () => {
        const size = useSmall ? calculateLongSide120(vid.videoWidth, vid.videoHeight) : calculateLongSide500(vid.videoWidth, vid.videoHeight);
        doUpload(size);
        vid.src = ''; vid.load();
      };
      vid.src = tempUrl;
    } else if (type === 'audio') {
      handleUpdateNode(id, { uploading: true, output: tempUrl, title: `上传中... ${file.name}` });
      try {
        const data = await api.presignUpload(file);
        if (data.url) {
          // 🔧 保留 tempUrl 作为 localCacheUrl
          handleUpdateNode(id, {
            uploading: false, output: data.url, outputs: [data.url], title: file.name,
            isDirectUpload: true, assetId: null, isDetached: false, fromModelNode: false,
            localCacheUrl: tempUrl
          });
          if (oldUrl && oldUrl.startsWith('blob:')) revokeSecureBlob(oldUrl);
          // 不立即删除旧文件 — 文件在删除项目时按文件夹整体清理
          setAlertMsg({ text: "音频已更换并上传", type: "success" });
          const fileType = localCacheService.getFileTypeFromMime(file.type);
          const fileName = localCacheService.extractFileNameFromUrl(data.url);
          if (fileName) localCacheService.writeFileToCache(crewIdRef.current, crewNameRef.current, fileType, fileName, file).catch(() => {});
          setTimeout(() => saveProject(false), 500);
        }
      } catch (err) {
        console.error("上传失败", err);
        handleUpdateNode(id, { uploading: false, title: oldTitle, output: oldUrl });
        revokeSecureBlob(tempUrl);
        const errMsg = err?.message || '上传失败';
        setAlertMsg({ text: errMsg.includes('云存储') ? '请先在设置中配置云存储' : `上传失败，已恢复原音频`, type: "error" });
      }
    }

    setReplacingNodeId(null);
  }, [replacingNodeId, createSecureBlob, revokeSecureBlob, handleUpdateNode, getNodes, saveProject, crewIdRef, crewNameRef, projectId, setAlertMsg]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // handleGenerate — AI 生成入口（前端侧）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 流程：
  //   1. 收集所有参考图/视频/音频 URL（从连线的源节点获取）
  //   2. 拼接 prompt（自动上下文 + 用户 prompt + 引用文本）
  //   3. 快照 modelName/modelLogo（防止生成中用户修改配置导致 UI 跳变）
  //   4. 设置 loading=true + generationStartTime（前端计时器依赖此字段）
  //   5. POST /api/generate（提交后立即返回，后续通过 WebSocket 接收进度/结果）
  //   6. 失败时恢复节点状态 + 停止边的传输动画
  //
  // generationRefIds 作用：
  //   记录本次生成消费了哪些参考图节点 ID，前端用于：
  //   - 在参考图节点上显示"已使用"标记
  //   - WebSocket 推送结果回来后，前端自行判断哪些参考图节点参与了生成
  const handleGenerate = useCallback(async (nodeId) => {
    const nodes = getNodes();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    // 统一控制连线传输动画，避免在多个分支重复同一段 setFlowEdges。
    const setNodeTransmitting = (isTransmitting) => {
      setFlowEdges(eds => eds.map(edge => {
        if (edge.target === nodeId) return { ...edge, data: { ...edge.data, isTransmitting } };
        return edge;
      }));
    };

    const isMjVendor = node.data?.vendor === 'yunwu-midjourney' || node.data?.vendor === 'apimart-midjourney';

    const isMjActionRetry = isMjVendor
      && !!node.data?.mjSourceNodeId
      && !!node.data?.mjActionType;

    const isLegacyMjRetryNode = isMjVendor
      && !!node.data?.mjSourceNodeId
      && !node.data?.mjActionType;

    if (isLegacyMjRetryNode) {
      const msg = '该 MJ 历史节点不包含重试动作参数，请回到上游节点重新点击对应 MJ 动作。';
      setAlertMsg({ text: msg, type: 'warning' });
      handleUpdateNode(nodeId, { loading: false, error: msg });
      return;
    }

    if (isMjActionRetry) {
      // MJ Action 节点重试：重放上一次 action，不再走 /api/generate。
      handleUpdateNode(nodeId, {
        loading: true,
        error: null,
        statusInfo: { status: '重试中...', progress: 0 },
        generationStartTime: Date.now(),
        generationModelName: node.data.modelName || 'Midjourney',
        generationModelLogo: node.data.modelLogo || null,
      });

      try {
        await saveProject(false);
      } catch (e) {
        console.warn('MJ 重试前自动保存失败（将继续尝试发起生成）:', e?.message || e);
      }

      setNodeTransmitting(true);

      try {
        const mjParams = node.data?.mjActionParams || {};
        await api.post('/api/mj-action', {
          projectId,
          sourceNodeId: node.data.mjSourceNodeId,
          targetNodeId: nodeId,
          actionType: node.data.mjActionType,
          index: mjParams.index || undefined,
          zoomLevel: mjParams.zoomLevel || undefined,
          panDirection: mjParams.panDirection || undefined,
        });
        setAlertMsg({ type: 'success', text: 'MJ 重试任务已提交到后台运行。' });
        return;
      } catch (err) {
        console.error('MJ 重试流程错误:', err);
        setAlertMsg({ text: 'MJ 重试失败: ' + err.message, type: 'error' });
        handleUpdateNode(nodeId, { loading: false, error: err.message || 'MJ 重试失败' });
        setNodeTransmitting(false);
        return;
      }
    }

    const imageFiles = node.data.refFiles?.filter(f => f.type === 'image') || [];
    const videoFiles = node.data.refFiles?.filter(f => f.type === 'video') || [];
    const audioFiles = node.data.refAudios || [];

    // 🔒 分镜锁定：只要有任意锁定格，且节点上有上一轮成品图（整张多宫格图），
    //   就把这张整图作为「参考图」多喂一张给图像模型（就这一张，不论锁了几格）。
    //   它不是上游连线节点，故不计入 consumedRefIds。lockReferenceFed 同步决定
    //   assembleStoryboardPrompt 是否把锁定格替换成「这部分与"参考图"保持一致」，
    //   两边同真同假，保证提示词引用的「参考图」一定被喂了进去。
    const hasLockedCell = !!node.data.storyboardMode
      && (node.data.gridCells || []).some(c => c && c.locked);
    const lockReferenceFed = hasLockedCell && !!node.data.output;
    const lockedRefFiles = lockReferenceFed
      ? [{ id: `lock-ref-${nodeId}`, type: 'image', url: node.data.output, localCacheUrl: node.data.localCacheUrl, title: LOCK_REF_NAME, _lockRef: true }]
      : [];
    // 上游参考图在前，上一轮整图在后 —— 用于 nodeParams.image 与图片说明
    const allImageFiles = [...imageFiles, ...lockedRefFiles];

    // 🆕 发给模型的图片说明：标题 + 描述词都用【完整原文】，不做截断。
    //    （控制台那行灰字仍旧截断显示，见 NodeConsole.js 的 imgExplanations —— 两边有意不一致）
    //    ⚠️ 只去文件扩展名，不能复用 formatShortTitle：那个函数会把标题砍成 5 字喂给模型。
    const stripExt = (s) => String(s || '').replace(/\.[^/.]+$/, '');

    // 🆕 描述词规范化：去掉 Markdown 标记再喂给模型
    //
    // 为什么需要：描述词常常是从别的大模型（ChatGPT/Claude/豆包…）复制过来的，
    // 自带 `## 标题` `**加粗**` `- 列表` `---` 这类 Markdown。这些标记拼进提示词后
    // 在厂商后台看是一坨乱码，模型也得自己猜哪些是内容哪些是格式。
    //
    // ⚠️ 只在**拼提示词时**清洗，不动库里存的原文：
    //    · 用户原文保持可读可编辑，随时能改回来
    //    · 对已存的描述词立即生效，不用回去逐条手改
    //    · 规则将来要调，改这个函数就行，数据没被洗坏
    //
    // 保守原则：只删格式标记，不删任何文字内容。有序列表 `1.` 故意保留 ——
    // 那个序号往往是有意义的（第几个人物、第几个镜头）。
    const normalizeDesc = (raw) => String(raw || '')
      // 代码围栏 ```lang → 整行删掉，里面的内容留着
      .replace(/^[ \t]*```.*$/gm, '')
      // 分隔线 --- / *** / ___（整行）→ 删
      .replace(/^[ \t]*([-*_])[ \t]*(?:\1[ \t]*){2,}$/gm, '')
      // ATX 标题 ## 标题 → 标题
      .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
      // 引用 > 内容 → 内容（可能多层 >>）
      .replace(/^[ \t]*>+[ \t]?/gm, '')
      // 无序列表 - / * / + 开头 → 删标记，保留文字
      .replace(/^[ \t]*[-*+][ \t]+/gm, '')
      // 链接/图片 [文字](url) 或 ![alt](url) → 只留文字
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
      // 加粗/斜体 **x** __x__ *x* _x_ → x
      //   注意顺序：先双标记再单标记，否则 **x** 会被单标记规则啃成 *x*
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/\*([^*\n]+)\*/g, '$1')
      // 单下划线斜体：只在词边界处理，避免误伤 snake_case 的标识符
      .replace(/(^|[\s，。、（(])_([^_\n]+)_(?=$|[\s，。、）)])/g, '$1$2')
      // 行内代码 `x` → x
      .replace(/`([^`\n]+)`/g, '$1')
      // 行尾的 Markdown 硬换行（两个空格）→ 单纯换行
      .replace(/[ \t]+$/gm, '')
      // 三行以上空行压成一个空行
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // 一条素材说明的组装：标题用直角引号「」而不是全角引号 “”
    //   —— 「」不是 JSON 特殊字符，厂商后台用 JSON.stringify 展示时不会变成 \” 一坨转义。
    // 描述词自带换行时（用户写了分段/Markdown）另起一行并缩进两格，
    //   让它明显属于上一条素材、而不是新起一条；单行描述词就跟在冒号后面，不浪费一行。
    const buildExplanation = (label, title, desc) => {
      const head = `${label}是「${title}」`;
      if (!desc) return head;
      return desc.includes('\n')
        ? `${head}:\n  ${desc.replace(/\n/g, '\n  ')}`
        : `${head}: ${desc}`;
    };

    const imgExplanations = allImageFiles.map((f, i) => {
      if (f._lockRef) return `图${i + 1}是「${LOCK_REF_NAME}」`;
      const t = stripExt(f.title) || '未命名';
      // descEnabled === false 表示用户在参考图卡片右下角关掉了这张图的描述词。
      // 默认（undefined）视为开启，老项目无需迁移。
      const d = f.descEnabled === false ? '' : normalizeDesc(f.description);
      return buildExplanation(`图${i + 1}`, t, d);
    });
    // 🆕 参考视频的描述词同样要进提示词：与图片同规则（完整原文、不截断、
    //    尊重 descEnabled 开关）。⚠️ 标题这里也不能用 formatShortTitle —— 那会砍成 5 字。
    const vidExplanations = videoFiles.map((f, i) => {
      const t = stripExt(f.title) || '未命名';
      const d = f.descEnabled === false ? '' : normalizeDesc(f.description);
      return buildExplanation(`视频${i + 1}`, t, d);
    });
    // ⚠️ 音频标题这里也要用 stripExt 而不是 formatShortTitle：
    //    后者会把标题砍成 5 字再喂给模型（那是控制台灰字的规则，不是提示词的）。
    const audioExplanations = audioFiles.map((a, i) => {
      const t = stripExt(a.title) || '未命名';
      const d = a.descEnabled === false ? '' : normalizeDesc(a.description);
      return buildExplanation(`音频${i + 1}`, t, d);
    });

    // 🆕 技能工具箱参考图的说明（加入 autoContext，与上游图保持一致的格式）
    const skillboxImgExplanations = [];
    if (node.data.skillboxPresetId != null && !node.data.skillboxDisabled && node.data.skillboxRefImageId) {
      const hit = findSkillboxPreset(callbacksRef?.current?.skillboxRef?.current, node.data.skillboxPresetId);
      const preset = hit?.preset;
      if (preset?.referenceEnabled && Array.isArray(preset.referenceImages)) {
        const refImage = preset.referenceImages.find(img => img.id === node.data.skillboxRefImageId);
        if (refImage) {
          const index = allImageFiles.length + 1;  // 上游图数量 + 1
          skillboxImgExplanations.push(`图${index}是”${refImage.name || '技能参考图'}”`);
        }
      }
    }

    // 一条素材一行：描述词可能自带多行，挤在一行里根本看不出条目边界。
    //   （控制台那行灰字是另一套代码、仍保持单行紧凑显示，两边互不影响）
    const autoContext = [
      ...imgExplanations,
      ...skillboxImgExplanations,  // 🆕 新增
      ...vidExplanations,
      ...audioExplanations
    ].join('\n');

    const tagTexts = node.data.refTexts?.map(t => t.text).filter(Boolean).join('\n') || '';

    // 🎬 分镜节点：用专用组装器把 basePrompts + gridCells 拼成完整 prompt，
    //   忽略 data.prompt（分镜节点没有 textarea）。其余引用上下文/标签文本仍照常拼接。
    let baseUserPrompt;
    if (node.data.storyboardMode) {
      const aspectRatio = node.data.params?.aspect_ratio || node.data.params?.size || '1:1';
      const gridType = node.data.gridType || 9;
      baseUserPrompt = assembleStoryboardPrompt(
        node.data.basePrompts || [],
        node.data.gridCells || [],
        aspectRatio,
        gridType,
        lockReferenceFed
      );
      // 🎬 从分镜草图工具箱读取基础提示词，附加到分镜内容前
      try {
        const { basePrompt } = await api.get('/api/ai/storyboard-base-prompt');
        if (basePrompt && basePrompt.trim()) {
          baseUserPrompt = `${basePrompt.trim()}\n\n${baseUserPrompt}`;
        }
      } catch (e) {
        console.error('[storyboard] 读取基础提示词失败:', e);
      }
      // 🎨 画面风格预设：按节点选中的 storyboardStyleId 取对应风格提示词，拼在最前层
      //   （风格词 → 分镜草图基础词 → 分镜内容）。未选或取不到则跳过。
      if (node.data.storyboardStyleId) {
        try {
          const { stylePresets } = await api.get('/api/ai/storyboard-style-presets');
          const hit = (Array.isArray(stylePresets) ? stylePresets : [])
            .find(s => String(s.id) === String(node.data.storyboardStyleId));
          if (hit && hit.prompt && hit.prompt.trim()) {
            baseUserPrompt = `${hit.prompt.trim()}\n\n${baseUserPrompt}`;
          }
        } catch (e) {
          console.error('[storyboard] 读取画面风格预设失败:', e);
        }
      }
    } else {
      baseUserPrompt = (node.data.prompt || '');
    }

    // 🎨 预设工具箱：按 presetId 实时取预设附加提示词，拼在最前（预设词在前）。
    //   实时从 presetsRef 三层树取，保证超管改后台后立即生效，无需重建节点。
    //   presetDisabled=true 时跳过注入（节点下拉顶部开关关闭了预设）。
    let presetPrompt = '';
    if (node.data.presetId != null && !node.data.presetDisabled) {
      const hit = findPreset(callbacksRef?.current?.presetsRef?.current, node.data.presetId);
      const preset = hit?.preset;
      if (preset && preset.prompt && preset.prompt.trim()) presetPrompt = preset.prompt.trim();
    }

    // ✨ 技能工具箱：按 skillboxPresetId 实时取技能附加提示词，拼在最前（与预设工具箱逻辑一致）。
    //   实时从 skillboxRef 三层树取，保证超管改后台后立即生效，无需重建节点。
    //   skillboxDisabled=true 时跳过注入（节点下拉顶部开关关闭了技能）。
    let skillboxPrompt = '';
    let skillboxRefPrompt = '';  // 🆕 参考图提示词
    if (node.data.skillboxPresetId != null && !node.data.skillboxDisabled) {
      const hit = findSkillboxPreset(callbacksRef?.current?.skillboxRef?.current, node.data.skillboxPresetId);
      const preset = hit?.preset;
      if (preset && preset.prompt && preset.prompt.trim()) skillboxPrompt = preset.prompt.trim();

      // 🆕 前端自动构造参考图提示词（带图片编号）
      if (node.data.skillboxRefImageId && preset?.referenceEnabled && Array.isArray(preset.referenceImages)) {
        const refImage = preset.referenceImages.find(img => img.id === node.data.skillboxRefImageId);
        if (refImage) {
          // 计算技能参考图在完整参考图数组中的位置（从1开始）
          const skillboxImageIndex = allImageFiles.length + 1;  // 上游图在前，技能图在后
          const refName = refImage.name || '参考图';
          const refPromptContent = refImage.prompt?.trim() || '';

          // 构造格式：图N是"参考图名称"，[自定义提示词]
          if (refPromptContent) {
            skillboxRefPrompt = `图${skillboxImageIndex}是"${refName}"，${refPromptContent}`;
          } else {
            skillboxRefPrompt = `图${skillboxImageIndex}是"${refName}"`;
          }
          console.log('[Skillbox RefImage] 参考图提示词已构造:', skillboxRefPrompt);
        }
      }
    }

    // 🔧 提示词拼接顺序：预设 → 技能 → 参考图 → 上游说明 → 用户输入 → 引用文本
    const finalPrompt = [presetPrompt, skillboxPrompt, skillboxRefPrompt, autoContext, baseUserPrompt, tagTexts ? `引用文本内容：\n${tagTexts}` : ''].filter(Boolean).join('\n\n');

    // 🆕 快照保存当前的 modelName 和 modelLogo，防止生成过程中修改配置导致 loading 界面跳变
    const snapshotName = node.data.modelName || "测试模型";
    const snapshotLogo = node.data.modelLogo || null;

    // 🆕 在生成开始时就记录参考图节点 ID，因为生成结果可能通过 WebSocket 推送回来
    // 如果放在 handleSuccessUpdate 里设，WebSocket 推送会覆盖掉
    const consumedRefIds = imageFiles.map(f => f.id);

    handleUpdateNode(nodeId, { 
      loading: true, 
      error: null,
      statusInfo: { status: '准备中...', progress: 0 },
      generationStartTime: Date.now(),
      generationModelName: snapshotName, // 🔒 锁定生成时的名称
      generationModelLogo: snapshotLogo, // 🔒 锁定生成时的Logo
      generationRefIds: consumedRefIds   // 🔒 锁定参考图 ID，标记为"已消费"
    });

    // 🛡️ 发起生成前先落库一次：确保 nodeId 在数据库存在，避免强刷后“任务消失”
    // 说明：saveProject 失败时不阻断生成，后端还会做 NODE_NOT_PERSISTED 兜底校验。
    try {
      await saveProject(false);
    } catch (e) {
      console.warn('生成前自动保存失败（将继续尝试发起生成）:', e?.message || e);
    }

    setNodeTransmitting(true);

    try {
      const count = node.data.batchCount || 1;
      const nodeParams = { ...(node.data.params || {}) };

      // 🆕 收集技能工具箱参考图
      const skillboxRefImageUrls = [];
      if (node.data.skillboxPresetId != null && !node.data.skillboxDisabled && node.data.skillboxRefImageId) {
        const hit = findSkillboxPreset(callbacksRef?.current?.skillboxRef?.current, node.data.skillboxPresetId);
        const preset = hit?.preset;
        if (preset?.referenceEnabled && Array.isArray(preset.referenceImages)) {
          const refImage = preset.referenceImages.find(img => img.id === node.data.skillboxRefImageId);
          if (refImage && refImage.fullUrl) {
            skillboxRefImageUrls.push(refImage.fullUrl);
            console.log('[Skillbox RefImage] 参考图 URL 已收集:', refImage.fullUrl, '名称:', refImage.name);
          }
        }
      }

      // 🔧 合并参考图：上游连线图片 + 技能工具箱参考图
      const allImageUrls = [
        ...allImageFiles.map(f => f.url),
        ...skillboxRefImageUrls
      ];

      if (allImageUrls.length > 0) nodeParams.image = allImageUrls;
      if (videoFiles.length > 0) nodeParams.video = videoFiles.map(f => f.url);
      if (audioFiles.length > 0) nodeParams.audio = audioFiles.map(a => a.url);

      // 🎨 LoRA 参数解析：从 node.data.params.loraStrengths 读取已选择的 LoRA
      const loraStrengths = node.data.params?.loraStrengths || {};
      const loraNames = Object.keys(loraStrengths);

      if (loraNames.length > 0) {
        console.log('[LoRA] Found LoRA selection');

        // 只取第一个 LoRA（目前后端只支持单个 lora 参数）
        const firstLoraName = loraNames[0]; // 这就是文件名
        const loraData = loraStrengths[firstLoraName];

        // 兼容新旧格式
        const strength = typeof loraData === 'number' ? loraData : loraData.strength;

        console.log('[LoRA] firstLoraName (filename):', firstLoraName);
        console.log('[LoRA] strength:', strength);

        nodeParams.lora = firstLoraName; // 直接传文件名
        if (strength !== undefined) {
          // 直接传递强度值，不做映射
          nodeParams.lora_strength = strength;
        }

        console.log('[LoRA] Final params:', { lora: nodeParams.lora, lora_strength: nodeParams.lora_strength });
      }

      // 🆕 调试日志：输出最终的提示词和参数

      const data = await api.post(`/api/generate`, {
        modelId: node.data.modelId || 'default-mock',
        prompt: finalPrompt,
        type: node.data.type || 'image',
        count,
        params: nodeParams,
        projectId, nodeId
      });

      if (data.status === 'processing') {
        setAlertMsg({ type: 'success', text: '任务已提交到后台运行，生成后将自动保存。您可以关闭页面。' });
        return;
      }

      if (data.success && data.urls && data.urls.length > 0) {
        const newOutputs = data.urls;
        const firstUrl = newOutputs[0];
        const isVideo = (node.data.type === 'video');

        if (data.errors && data.errors.length > 0) {
          setAlertMsg({ text: `部分任务失败 (${data.errors.length}): ${data.errors[0]}`, type: 'warning' });
        }

        // 检查节点是否有用户选择的比例参数（ratio/aspect_ratio/size 等）
        const hasUserSelectedRatio = node.data.params && (
          node.data.params.ratio ||
          node.data.params.aspect_ratio ||
          node.data.params.size
        );

        const handleSuccessUpdate = (size) => {
          // 如果用户选择了比例，保持原尺寸；否则根据实际图片尺寸调整
          const finalUpdate = hasUserSelectedRatio
            ? { loading: false, outputs: newOutputs, output: firstUrl }
            : { loading: false, outputs: newOutputs, output: firstUrl, customSize: size };
          handleUpdateNode(nodeId, finalUpdate);
          setNodeTransmitting(false);
        };

        if (isVideo) {
          const vid = document.createElement('video');
          vid.onloadedmetadata = () => { handleSuccessUpdate(calculateLongSide500(vid.videoWidth, vid.videoHeight)); vid.src = ''; vid.load(); };
          vid.onerror = () => { handleSuccessUpdate({ width: 380, height: 214 }); vid.src = ''; vid.load(); };
          vid.src = firstUrl;
        } else {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => { handleSuccessUpdate(calculateLongSide500(img.width, img.height)); img.src = ''; };
          img.onerror = () => {
            handleUpdateNode(nodeId, { loading: false, outputs: newOutputs, output: firstUrl });
            setNodeTransmitting(false);
            img.src = '';
          };
          img.src = firstUrl;
        }
      } else {
        throw new Error(data.error || (data.errors ? data.errors.join(', ') : "生成无返回结果"));
      }
    } catch (err) {
      console.error("生成流程错误:", err);
      setAlertMsg({ text: "AI 生成失败: " + err.message, type: 'error' });
      handleUpdateNode(nodeId, { loading: false });
      setNodeTransmitting(false);
    }
  }, [getNodes, handleUpdateNode, setFlowEdges, projectId, setAlertMsg]);

  // --- Detach（剥离媒体到独立节点）---
  // 用途：视频截帧、音频快剪导出、控制台引用分离、节点间媒体复制
  // mediaUrl 来源：
  //   - blob: URL → 视频截帧（canvas.toBlob）、用户拖拽文件 → 触发 uploadAndReplace 上传云存储
  //   - 云存储 URL → 音视频快剪后端已上传 → 不触发前端上传（直接使用）
  //   - data: URL → 安全网兜底（转为 blob: 后上传）
  const handleDetachImage = useCallback(async (sourceNodeId, mediaUrl, dropPos, targetType = 'image', options = {}) => {
    const sourceNode = getNodes().find(n => n.id === sourceNodeId);
    const originalTitle = sourceNode?.data?.title || '剥离素材';
    const id = `node-${Date.now()}`;
    const basePos = (dropPos && dropPos.x !== 0) ? dropPos : sourceNode.position;
    const newNodePosition = { x: basePos.x + 320, y: basePos.y - 120 };
    const isSourceModelNode = sourceNode && !sourceNode.data.isDirectUpload && !sourceNode.data.assetId;

    // ── 裁剪模式（快剪导出）──
    // 节点创建时为 loading 状态（封面缩略图 + Loader2 动画），等待异步裁剪完成后更新
    const isClipping = !!options.clipping;

    // 裁剪模式：使用传入的视频尺寸计算节点比例（保持与原始视频相同的宽高比）
    let clippingCustomSize = targetType === 'audio' ? { width: 200, height: 56 } : { width: 240, height: 240 };
    if (isClipping && options.videoWidth && options.videoHeight) {
      clippingCustomSize = calculateLongSide120(options.videoWidth, options.videoHeight);
    }

    // 🛡️ 跟踪 detach 产生的 blob URL，确保节点删除/替换时能正确释放
    if (mediaUrl && mediaUrl.startsWith('blob:')) {
      activeBlobsRef.current.add(mediaUrl);
    }

    const newNode = {
      id, type: 'aiNode', position: newNodePosition, selected: true,
      data: {
        type: targetType, title: originalTitle,
        output: isClipping ? null : mediaUrl,
        outputs: isClipping ? [] : [mediaUrl],
        isDirectUpload: true, isDetached: true, fromModelNode: isSourceModelNode,
        customSize: isClipping ? clippingCustomSize : (targetType === 'audio' ? { width: 200, height: 56 } : { width: 240, height: 240 }),
        // 裁剪 loading 态：封面缩略图 + 半透明 Loader2 动画叠加（与智能抠图风格一致）
        ...(isClipping ? {
          loading: true,              // 触发 loading 遮罩
          clipping: true,             // 标记裁剪模式（CustomNode 据此切换轻量动画）
          clippingThumbnail: options.thumbnail || null,  // 裁剪起始帧截图（dataURL）
          statusInfo: { status: '正在裁剪...' },
          generationStartTime: Date.now(),
          generationModelName: '✂️ 快剪',
        } : {}),
        onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton, onReupload: handleReuploadLogic,
        onGenerate: handleGenerate, onReplace: handleReplace, onRemoveRefAudio: handleRemoveRefAudio,
        onHandleDoubleClick: (e, id) => {
          const sNode = getNodes().find(n => n.id === id);
          setMenuData({ mouseX: e.clientX + 20, mouseY: e.clientY + 20, sourceId: id, sourceType: sNode.data.type, sourceNodePos: sNode.position });
        },
        onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText, onDetach: handleDetachImage,
        onDescribe: callbacksRef?.current?.onDescribe,
        onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
        getMjDescribeConfig: () => callbacksRef?.current?.mjDescribeConfigRef?.current,
        onApplyPreset: (...args) => callbacksRef?.current?.onApplyPreset?.(...args),
        // 🐛 修复（2026-10-27）：裁剪/剥离出的新节点缺技能工具箱回调
        //    → 技能工具箱横条 shouldShow 条件里的 data.onApplySkillbox 为 undefined
        //    → 工具条不显示，只有刷新页面（走 callbacksRef 全量路径）才出现。
        //    上一次修复（233eabe）只补了 buildNodeData（上传路径），漏了本函数。
        onApplySkillbox: (...args) => callbacksRef?.current?.onApplySkillbox?.(...args),
        getSkillbox: () => callbacksRef?.current?.skillboxRef?.current || { toolsets: [] },
        getPresets: () => callbacksRef?.current?.presetsRef?.current || [],
      }
    };

    setNodes(nds => [...nds.map(n => {
      if (n.id === sourceNodeId) {
        // 裁剪模式：不修改源节点的 outputs（裁剪不移除原始内容）
        if (isClipping) return { ...n, selected: false };
        if (sourceNode.data.type === 'video' && targetType === 'image') return { ...n, selected: false };
        const newOutputs = (n.data.outputs || []).filter(u => u !== mediaUrl);
        return { ...n, selected: false, data: { ...n.data, outputs: newOutputs, output: newOutputs[0] || null } };
      }
      return { ...n, selected: false };
    }), newNode]);

    const newEdge = {
      id: `edge-locked-${sourceNodeId}-${id}`, source: sourceNodeId, target: id,
      type: 'custom', data: { locked: true, originalColor: C.EDGE_COLOR_AUTO },
      style: { stroke: C.EDGE_COLOR_AUTO, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE }
    };
    setFlowEdges(eds => [...eds, newEdge]);

    // 裁剪模式：跳过上传和自动尺寸（后续异步完成后由调用方更新）
    if (!isClipping) {
      if (mediaUrl && (mediaUrl.startsWith('blob:') || mediaUrl.startsWith('data:'))) {
        try {
          const res = await fetch(mediaUrl);
          const blob = await res.blob();
          const ext = blob.type.split('/')[1] || 'png';
          const fileName = `snapshot-${Date.now()}.${ext}`;
          const file = new File([blob], fileName, { type: blob.type });
          // ✅ data: URL 也需转为 blob: URL 作为临时显示 URL
          const displayUrl = mediaUrl.startsWith('data:') ? URL.createObjectURL(blob) : mediaUrl;
          uploadAndReplace(id, file, displayUrl, originalTitle);
        } catch (e) {
          console.error("Auto-upload snapshot failed", e);
        }
      }

      if (targetType !== 'audio') {
        if (targetType === 'video') {
          const vid = document.createElement('video');
          vid.onloadedmetadata = () => { handleUpdateNode(id, { customSize: calculateLongSide120(vid.videoWidth, vid.videoHeight) }); vid.src = ''; vid.load(); };
          vid.src = mediaUrl;
        } else {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => { handleUpdateNode(id, { customSize: calculateLongSide120(img.width, img.height) }); img.src = ''; };
          img.src = mediaUrl;
        }
      }
    }

    return id; // 返回新节点 ID，供异步裁剪流程使用
  }, [handleUpdateNode, handleReuploadLogic, handleDeleteNodeButton, handleGenerate, handleReplace, handleRemoveRefAudio, getNodes, setNodes, setFlowEdges, handleRemoveRefFile, handleRemoveRefText, uploadAndReplace, setMenuData, callbacksRef]);

  // --- handleHandleDoubleClick ---
  const handleHandleDoubleClick = useCallback((event, nodeId) => {
    event.stopPropagation();
    const sourceNode = getNodes().find(n => n.id === nodeId);
    if (!sourceNode) return;
    const mousePos = { x: event.clientX, y: event.clientY };
    setMenuData({
      mouseX: mousePos.x + 20, mouseY: mousePos.y + 20,
      flowPos: screenToFlowPosition(mousePos),
      sourceId: nodeId, sourceType: sourceNode.data.type,
      sourceTitle: sourceNode.data.title, sourceNodePos: sourceNode.position
    });
  }, [getNodes, screenToFlowPosition, setMenuData]);

  // --- handleDisconnectDownstream: 断开节点下游所有连线（完整清理引用） ---
  const handleDisconnectDownstream = useCallback((nodeId) => {
    // 1️⃣ 找到所有下游边和目标节点
    const downstreamEdges = [];
    const targetNodeIds = new Set();
    
    setEdgesState(eds => {
      eds.forEach(e => {
        if (e.source === nodeId) {
          downstreamEdges.push(e);
          targetNodeIds.add(e.target);
        }
      });
      return eds.filter(e => e.source !== nodeId);
    });
    
    // 2️⃣ 从下游节点的 refFiles/refTexts/refAudios 中移除引用
    if (targetNodeIds.size > 0) {
      setNodes(nds => {
        const sourceNode = nds.find(n => n.id === nodeId);
        if (!sourceNode) return nds;
        
        return nds.map(node => {
          if (targetNodeIds.has(node.id)) {
            const newData = { ...node.data };
            if (sourceNode.data.type === 'text') {
              newData.refTexts = (newData.refTexts || []).filter(t => t.id !== nodeId);
            } else if (sourceNode.data.type === 'audio') {
              newData.refAudios = (newData.refAudios || []).filter(a => a.id !== nodeId);
            } else {
              newData.refFiles = (newData.refFiles || []).filter(f => f.id !== nodeId);
            }
            return { ...node, data: newData };
          }
          return node;
        });
      });
    }
  }, [setEdgesState, setNodes]);

  return {
    // blob management
    createSecureBlob, revokeSecureBlob,
    // upload abort
    abortNodeUpload,
    // node CRUD
    handleUpdateNode, handleDeleteNodeButton, handleDeleteSelectedNodes, handleConfirmDelete,
    // ref management
    handleRemoveRefAudio, handleRemoveRefFile, handleRemoveRefText,
    // file operations
    handleReuploadLogic, handleReplace, handleReplaceFileChange, uploadAndReplace,
    // detach & generate
    handleDetachImage, handleGenerate, handleDownscaleInPlace,
    // disconnect
    handleDisconnectDownstream,
    // UI
    handleHandleDoubleClick,
  };
};

export default useNodeActions;
